import { MenuList } from './Menu';

/** Resolve corporate vs personal Credentials Vault nav for sidebar highlighting. */
export function getCredentialVaultNavSideFromPath(pathname, search = '', state = null) {
  if (!pathname) return null;
  const trimmed =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const parts = trimmed.split('/').filter(Boolean);
  if (parts[0] !== 'credential-vault') return null;
  const seg = parts[1];
  if (!seg || seg === 'vaultSettings') return null;

  if (seg === 'credentialVaultList') return 'corporate';
  if (seg === 'personalVaultList') return 'personal';

  const sideFromState = state && state.credentialVaultSide;
  if (sideFromState === 'personal' || sideFromState === 'corporate') return sideFromState;

  if (seg === 'addCredential') {
    try {
      const q = search.startsWith('?') ? search.slice(1) : search || '';
      const qs = new URLSearchParams(q);
      if (qs.get('scope') === 'personal') return 'personal';
    } catch (_) {
      /* ignore */
    }
    return 'corporate';
  }

  return null;
}

export function normalizePathForComparison(path) {
  if (!path) return '';
  const trimmed = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
}

export function getBasePathPrefix(path) {
  if (!path) return null;
  const normalized = normalizePathForComparison(path);
  const parts = normalized.split('/').filter((p) => p);
  if (parts.length >= 1) {
    return '/' + parts[0];
  }
  return normalized;
}

/** Project routes live under `/task/*project*` but belong to the Projects sidebar module. */
export function isProjectTaskPath(pathname) {
  const parts = normalizePathForComparison(pathname).split('/').filter(Boolean);
  const seg = parts[1]?.toLowerCase() || '';
  return parts[0] === 'task' && seg.includes('project');
}

export function isDashboardPath(pathname) {
  const normalized = normalizePathForComparison(pathname);
  return normalized === '/dashboard';
}

/**
 * Paths that belong to the Settings shell (landing + linked pages).
 * Sidebar shows main modules on these routes instead of Settings children.
 */
export function isSettingsShellPath(pathname) {
  const normalized = normalizePathForComparison(pathname);
  if (!normalized) return false;

  if (normalized === '/settings' || normalized.startsWith('/settings/')) return true;
  if (normalized === '/edit-profile') return true;
  if (normalized.toLowerCase() === '/changepassword') return true;
  if (normalized === '/user-subscription') return true;
  if (normalized === '/credits' || normalized.startsWith('/credits/')) return true;
  if (normalized === '/staff' || normalized.startsWith('/staff/')) return true;
  if (normalized === '/vehicle' || normalized.startsWith('/vehicle/')) return true;
  if (
    normalized === '/credential-vault/vaultSettings' ||
    normalized.startsWith('/credential-vault/vaultSettings/')
  ) {
    return true;
  }

  return false;
}

/**
 * First accessible child route for a module (already filtered list).
 * @param {object} module - MenuList parent
 * @param {Array} visibleSubItems - filtered accessible children
 * @returns {string|null}
 */
export function getModuleEntryPath(module, visibleSubItems) {
  if (!Array.isArray(visibleSubItems) || visibleSubItems.length === 0) {
    return module?.to || null;
  }
  const first = visibleSubItems.find((item) => item.to && item.to !== '#');
  if (!first) return module?.to || null;
  const to = first.to;
  return to.startsWith('/') ? to : `/${to}`;
}

function findBestSubItemMatch(normalizedPath, menuItem, search = '', locationState = null) {
  const currentPath = normalizePathForComparison(normalizedPath);
  let bestMatch = null;
  let bestMatchScore = 0;

  const currentParts = currentPath.split('/').filter((p) => p);

  for (const subItem of menuItem.content || []) {
    if (!subItem.to) continue;

    const subItemPath = normalizePathForComparison(subItem.to);
    const subItemParts = subItemPath.split('/').filter((p) => p);

    if (subItem.title === 'Staff' && currentParts[0] === 'staff') {
      if (1000 > bestMatchScore) {
        bestMatch = subItem.title;
        bestMatchScore = 1000;
      }
    }

    if (subItem.title === 'Vehicle' && currentParts[0] === 'vehicle') {
      if (1000 > bestMatchScore) {
        bestMatch = subItem.title;
        bestMatchScore = 1000;
      }
    }

    if (
      subItem.title === 'Credentials Vault' &&
      currentParts[0] === 'credential-vault' &&
      currentParts[1] === 'vaultSettings'
    ) {
      if (1100 > bestMatchScore) {
        bestMatch = subItem.title;
        bestMatchScore = 1100;
      }
    }

    if (
      subItem.menu_key === 'credential_vault_list' &&
      currentParts[0] === 'credential-vault' &&
      currentParts[1] !== 'vaultSettings'
    ) {
      const navSide = getCredentialVaultNavSideFromPath(normalizedPath, search, locationState);
      const listSeg = subItemParts[1];
      const matches =
        (navSide === 'corporate' && listSeg === 'credentialVaultList') ||
        (navSide === 'personal' && listSeg === 'personalVaultList');
      if (matches && 1000 > bestMatchScore) {
        bestMatch = subItem.title;
        bestMatchScore = 1000;
      }
    }

    if (
      subItem.title === 'Performance Report' &&
      currentParts[0] === 'task' &&
      currentParts[1] === 'performance'
    ) {
      if (1000 > bestMatchScore) {
        bestMatch = subItem.title;
        bestMatchScore = 1000;
      }
    }

    if (
      (subItem.menu_key === 'project' ||
        subItem.title === 'Project List' ||
        subItem.title === 'Project ' ||
        subItem.title === 'Project' ||
        subItem.title === 'Create New Project') &&
      currentParts[0] === 'task' &&
      currentParts[1]?.toLowerCase().includes('project')
    ) {
      // Prefer exact list vs add routes when both share the project menu key
      if (subItem.title === 'Create New Project' && currentParts[1]?.toLowerCase() === 'addproject') {
        if (1100 > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = 1100;
        }
      } else if (
        subItem.title !== 'Create New Project' &&
        currentParts[1]?.toLowerCase() !== 'addproject' &&
        1000 > bestMatchScore
      ) {
        bestMatch = subItem.title;
        bestMatchScore = 1000;
      }
    }

    if (currentParts[0] === 'forum') {
      if (
        subItem.menu_key === 'forum_list' &&
        currentParts[1] === 'dashboard' &&
        String(subItem.to || '').includes('/forum/dashboard')
      ) {
        if (1000 > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = 1000;
        }
      }
      if (
        subItem.menu_key === 'forum_list' &&
        (currentParts[1] === 'list' || currentParts[1] === 'detail') &&
        String(subItem.to || '').includes('/forum/list')
      ) {
        if (1000 > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = 1000;
        }
      }
      if (
        subItem.menu_key === 'forum_create' &&
        (currentParts[1] === 'create' || currentParts[1] === 'edit')
      ) {
        if (1000 > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = 1000;
        }
      }
    }

    if (currentPath === subItemPath || currentPath.startsWith(subItemPath + '/')) {
      if (subItemPath.length > bestMatchScore) {
        bestMatch = subItem.title;
        bestMatchScore = subItemPath.length;
      }
    }

    if (currentParts[0] === 'documents') {
      const documentAction = currentParts[1]?.toLowerCase() || '';
      if (['upload', 'details', 'folder'].includes(documentAction) && subItem.title === 'My Documents') {
        const score = subItemPath.length;
        if (score > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = score;
        }
      }
    }

    if (currentParts.length >= 2 && subItemParts.length >= 2) {
      const currentParent = currentParts[0];
      const subItemParent = subItemParts[0];

      if (currentParent === subItemParent) {
        const currentKey =
          currentParts[1]?.toLowerCase().replace(/^(add|edit)/, '').replace(/list$/, '') || '';
        const subItemKey = subItemParts[1]?.toLowerCase().replace(/list$/, '') || '';

        if (currentKey && subItemKey && currentKey === subItemKey) {
          const score = subItemPath.length;
          if (score > bestMatchScore) {
            bestMatch = subItem.title;
            bestMatchScore = score;
          }
        }
      }
    }
  }

  return bestMatch;
}

/**
 * Resolve which MenuList module matches the current path.
 * @returns {{ menuTitle: string, submenuTitle: string, module: object }|null}
 */
export function getActiveModule(pathname, search = '', locationState = null) {
  if (isDashboardPath(pathname)) return null;

  // Settings shell is not a sidebar module — avoid matching Settings children
  if (isSettingsShellPath(pathname)) return null;

  const normalizedPath =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const currentPath = normalizePathForComparison(normalizedPath);

  for (const menuItem of MenuList) {
    // Settings is hub-only (navbar gear); never treat as active sidebar module
    if (menuItem.title === 'Settings') continue;
    if (!menuItem.content || menuItem.content.length === 0) continue;

    const pathPrefixes = new Set();
    for (const subItem of menuItem.content) {
      if (subItem.to) {
        const basePrefix = getBasePathPrefix(subItem.to);
        if (basePrefix) pathPrefixes.add(basePrefix);
      }
    }

    let matchedPrefix = null;
    for (const prefix of pathPrefixes) {
      if (currentPath.startsWith(prefix + '/') || currentPath === prefix) {
        matchedPrefix = prefix;
        break;
      }
    }

    if (matchedPrefix) {
      // Tasks and Projects share the `/task` prefix — keep them distinct in the sidebar.
      const onProjectPath = isProjectTaskPath(currentPath);
      if (menuItem.menu_key === 'tasks' && onProjectPath) continue;
      if (menuItem.menu_key === 'projects' && !onProjectPath) continue;

      const bestMatch = findBestSubItemMatch(normalizedPath, menuItem, search, locationState);
      let defaultSubItem = menuItem.content[0]?.title || '';

      if (!bestMatch && menuItem.title === 'Documents') {
        const myDocumentsItem = menuItem.content.find((item) => item.title === 'My Documents');
        if (myDocumentsItem) defaultSubItem = myDocumentsItem.title;
      }

      if (!bestMatch && menuItem.title === 'Credentials Vault') {
        const navSide = getCredentialVaultNavSideFromPath(normalizedPath, search, locationState);
        if (navSide === 'personal') {
          const hit = menuItem.content.find((i) => (i.to || '').includes('personalVaultList'));
          if (hit) defaultSubItem = hit.title;
        } else if (navSide === 'corporate') {
          const hit = menuItem.content.find((i) => (i.to || '').includes('credentialVaultList'));
          if (hit) defaultSubItem = hit.title;
        } else {
          defaultSubItem = '';
        }
      }

      return {
        menuTitle: menuItem.title,
        submenuTitle: bestMatch || defaultSubItem,
        module: menuItem,
      };
    }

    for (const subItem of menuItem.content) {
      const subItemPath = normalizePathForComparison(subItem.to);
      if (
        subItem.to &&
        (currentPath === subItemPath || currentPath.startsWith(subItemPath + '/'))
      ) {
        return {
          menuTitle: menuItem.title,
          submenuTitle: subItem.title,
          module: menuItem,
        };
      }

      if (subItem.content && subItem.content.length > 0) {
        for (const nestedItem of subItem.content) {
          const nestedPath = normalizePathForComparison(nestedItem.to);
          if (
            nestedItem.to &&
            (currentPath === nestedPath || currentPath.startsWith(nestedPath + '/'))
          ) {
            return {
              menuTitle: menuItem.title,
              submenuTitle: subItem.title,
              module: menuItem,
            };
          }
        }
      }
    }
  }

  return null;
}

/** Display label for navbar brand / module title. */
export function getModuleDisplayName(pathname, search = '', locationState = null) {
  if (isDashboardPath(pathname)) return 'Flowdo Business';
  if (isSettingsShellPath(pathname)) return 'Settings';
  const active = getActiveModule(pathname, search, locationState);
  if (!active) return 'Flowdo Business';
  if (active.menuTitle === 'Discussion Forum') return 'Forum';
  if (active.menuTitle === 'Credentials Vault') return 'Vault';
  if (active.menuTitle === 'Snap Bill') return 'Snap Bill';
  if (active.menuTitle === 'Projects') return 'Projects';
  return active.menuTitle;
}

/** Settings entry for navbar gear — always the Settings landing hub. */
export function getSettingsEntryPath() {
  return '/settings';
}
